import { isBlueInput, isBluRC } from 'src/helpers/interface_helpers';
import { get, writable } from 'svelte/store';

interface discoveredDevice {
	addr: string;
	encrypted: boolean;
	local_name: string;
	rssi: number;
	shelly_mfdata: {
		model_id: number;
		flags: number;
		mac: string;
	};
}

type DiscoveryStatus = 'idle' | 'discovering' | 'success' | 'error' | 'timeout' | 'stopped';

interface DeviceDiscoveryState {
	discoveredDevices: discoveredDevice[]; // Store full device objects instead of just strings
	runningDiscovery: boolean;
	clearedDevices: boolean;
	error?: string; // Add error state
	lastDiscoveryTime?: number; // Track when discovery was last started
	status?: DiscoveryStatus; // Track discovery status
	lastEventType?: string; // Track last socket event type (device_discovered, component_added, etc.)
	componentAdded?: boolean; // Track if component was added successfully
	listRefreshTrigger?: number; // Timestamp to trigger list refresh
}

/**
 * Creates a store for managing BTHome device discovery state across multiple host devices.
 * Tracks discovered devices, discovery status, errors, and triggers for list refreshes.
 *
 * @returns Store with methods for managing discovery state per device
 */
const createBtHomeDiscoveredDevices = () => {
	const discoveredDevices: { [id: string]: DeviceDiscoveryState } = {};

	const { subscribe, set, update } = writable(discoveredDevices);

	return {
		subscribe,
		/**
		 * Adds a discovered BTHome device to the host device's discovery list.
		 * Updates or adds the device and marks discovery as successful.
		 *
		 * @param {string} id - Host device ID
		 * @param {discoveredDevice | string} message - Discovered device object or legacy string identifier
		 */
		addDevice: (id: string, message: discoveredDevice | string) => {
			if (!id) return;

			update((devices) => {
				// Ensure device state exists
				if (!devices[id]) {
					devices[id] = {
						discoveredDevices: [],
						runningDiscovery: false,
						clearedDevices: false,
					};
				}

				console.warn('[BT HOME] Adding device:', id, message);

				if (typeof message === 'string') {
					if (message === 'bthomecontrol:200') {
						devices[id].discoveredDevices.push({
							addr: 'unknown',
							encrypted: false,
							local_name: 'BTHome Control',
							rssi: 0,
							shelly_mfdata: { model_id: 0, flags: 0, mac: 'unknown' },
						});
					}
				} else if (message && typeof message === 'object') {
					// Handle full device object
					const existingIndex = devices[id].discoveredDevices.findIndex(
						(d) => d.addr === message.addr,
					);

					if (existingIndex !== -1) {
						// Update existing device
						devices[id].discoveredDevices[existingIndex] = message;
					} else {
						// Add new device
						devices[id].discoveredDevices.push(message);
					}
				}

				// Clear any previous errors and update status
				devices[id].error = undefined;
				devices[id].status = 'success';
				devices[id].lastEventType = 'device_discovered';

				console.warn('[BT HOME] Updated devices for', id, ':', devices[id].discoveredDevices);
				return devices;
			});
		},
		/**
		 * Gets the list of discovered devices for a host device.
		 *
		 * @param {string} id - Host device ID
		 * @returns {discoveredDevice[]} Array of discovered devices
		 */
		getDevices: (id: string) => {
			return discoveredDevices[id]?.discoveredDevices || [];
		},
		/**
		 * Gets the count of discovered devices for a host device.
		 *
		 * @param {string} id - Host device ID
		 * @returns {number} Number of discovered devices
		 */
		getDeviceCount: (id: string) => {
			return discoveredDevices[id]?.discoveredDevices?.length || 0;
		},
		/**
		 * Gets the full discovery state for a host device.
		 *
		 * @param {string} id - Host device ID
		 * @returns {DeviceDiscoveryState | null} Discovery state or null if not found
		 */
		getDiscoveryState: (id: string) => {
			return discoveredDevices[id] || null;
		},
		/**
		 * Initializes discovery mode for a host device.
		 * Clears any previous discovered devices and sets status to discovering.
		 *
		 * @param {string} id - Host device ID
		 */
		initDiscovery: (id: string) => {
			if (!id) return;
			update((devices) => {
				if (!devices[id]) {
					devices[id] = {
						discoveredDevices: [],
						runningDiscovery: false,
						clearedDevices: false,
					};
				}
				// Initialize discovery state
				devices[id] = {
					discoveredDevices: [],
					runningDiscovery: true,
					clearedDevices: false,
					lastDiscoveryTime: Date.now(),
					error: undefined,
					status: 'discovering',
					lastEventType: undefined,
					componentAdded: false,
				};
				console.warn('[BT HOME] Discovery initialized for', id);
				return devices;
			});
		},
		/**
		 * Clears the discovered devices list for a host device.
		 * Sets clearedDevices flag to trigger UI refresh.
		 *
		 * @param {string} id - Host device ID
		 */
		clearDevices: (id) => {
			update((devices) => {
				if (devices?.[id]) {
					devices[id].discoveredDevices = [];
					devices[id].clearedDevices = true;
					devices[id].error = undefined;
					devices[id].lastEventType = 'component_removed';
					console.warn('[BT HOME] Devices cleared for', id);
				}
				return devices;
			});
		},
		/**
		 * Removes a specific discovered device by its address.
		 *
		 * @param {string} id - Host device ID
		 * @param {string} addr - Device address to remove
		 */
		removeDeviceByAddr: (id: string, addr: string) => {
			update((devices) => {
				if (devices?.[id] && addr) {
					devices[id].discoveredDevices = devices[id].discoveredDevices.filter(
						(d) => d.addr !== addr,
					);
					console.warn('[BT HOME] Removed device by address for', id, ':', addr);
				}
				return devices;
			});
		},
		/**
		 * Stops the discovery process for a host device.
		 * Updates status based on whether devices were found.
		 *
		 * @param {string} id - Host device ID
		 */
		stopDiscovery: (id: string) => {
			update((devices) => {
				if (devices?.[id]) {
					devices[id].runningDiscovery = false;
					devices[id].clearedDevices = false;
					if (devices[id].status === 'discovering') {
						devices[id].status =
							devices[id].discoveredDevices.length > 0 ? 'success' : 'stopped';
					}
					if (!devices[id].lastEventType) {
						devices[id].lastEventType = 'discovery_done';
					}
				}
				return devices;
			});
		},
		/**
		 * Sets an error state for discovery on a host device.
		 * Stops discovery and updates status to error.
		 *
		 * @param {string} id - Host device ID
		 * @param {string} error - Error message
		 */
		setError: (id: string, error: string) => {
			update((devices) => {
				if (devices?.[id]) {
					devices[id].error = error;
					devices[id].runningDiscovery = false;
					devices[id].status = 'error';
					console.warn('[BT HOME] Error set for', id, ':', error);
				}
				return devices;
			});
		},
		/**
		 * Marks that a BTHome component was successfully added to the host device.
		 * Updates status to success and clears any errors.
		 *
		 * @param {string} id - Host device ID
		 * @param {boolean} added - Whether component was added successfully
		 */
		setComponentAdded: (id: string, added: boolean) => {
			update((devices) => {
				if (devices?.[id]) {
					devices[id].componentAdded = added;
					devices[id].lastEventType = 'component_added';
					if (added) {
						devices[id].status = 'success';
						devices[id].error = undefined;
					}
					console.warn('[BT HOME] Component added status set for', id, ':', added);
				}
				return devices;
			});
		},
		/**
		 * Marks discovery as timed out for a host device.
		 * Stops discovery and updates status to timeout.
		 *
		 * @param {string} id - Host device ID
		 */
		setTimeout: (id: string) => {
			update((devices) => {
				if (devices?.[id]) {
					devices[id].status = 'timeout';
					devices[id].runningDiscovery = false;
					console.warn('[BT HOME] Timeout set for', id);
				}
				return devices;
			});
		},
		/**
		 * Clears all discovery state for all devices.
		 */
		clearAllDevices: () => {
			set({});
		},
		/**
		 * Clears error and timeout states for a host device.
		 * Resets status to idle and clears any error messages.
		 *
		 * @param {string} id - Host device ID
		 */
		clearErrors: (id: string) => {
			if (!id) return;
			update((devices) => {
				if (devices?.[id]) {
					devices[id].error = undefined;
					if (devices[id].status === 'timeout' || devices[id].status === 'error') {
						devices[id].status = 'idle';
					}
				}
				return devices;
			});
		},
		/**
		 * Signals that the paired devices list should be refreshed for a host device.
		 * Updates listRefreshTrigger to trigger reactive statements.
		 *
		 * @param {string} id - Host device ID
		 */
		signalListRefresh: (id: string) => {
			update((devices) => {
				if (devices[id]) {
					devices[id].listRefreshTrigger = Date.now();
				} else {
					devices[id] = {
						discoveredDevices: [],
						runningDiscovery: false,
						clearedDevices: false,
						listRefreshTrigger: Date.now(),
					};
				}
				return devices;
			});
		},
	};
};
export const btHomeDiscoveredDevices = createBtHomeDiscoveredDevices();
